import React, { useEffect, useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import "./index.css";
import { CartList } from "../../views/CartController/CartList";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useFirebaseCartById } from "../../hooks/useCartDetail";

const containerStyle = {
  width: "100%",
  height: "70vh",
};

const defaultCenter = {
  lat: 6.9270786,
  lng: 79.861243,
};

const parseCoordinate = (coordStr: string): number => {
  if (!coordStr) return 0;
  const match = coordStr.match(/^([\d.]+)/);
  if (match) {
    const value = parseFloat(match[1]);
    if (coordStr.includes('S') || coordStr.includes('W')) {
      return -value;
    }
    return value;
  }
  return 0;
};

const extractLatLng = (location: any): { lat: number; lng: number } | null => {
  if (!location) return null;

  // Case 1: Array format like [6.9248884° N, 79.8550434° E]
  if (Array.isArray(location) && location.length >= 2) {
    return {
      lat: parseCoordinate(location[0].toString()),
      lng: parseCoordinate(location[1].toString())
    };
  }

  // Case 2: Object with _lat and _long
  if (location._lat !== undefined && location._long !== undefined) {
    return {
      lat: location._lat,
      lng: location._long
    };
  }

  // Case 3: Object with _latitude and _longitude
  if (location._latitude !== undefined && location._longitude !== undefined) {
    return {
      lat: location._latitude,
      lng: location._longitude
    };
  }

  // Case 4: Object with latitude and longitude
  if (location.latitude !== undefined && location.longitude !== undefined) {
    return {
      lat: location.latitude,
      lng: location.longitude
    };
  }

  // Case 5: Object with lat and lng
  if (location.lat !== undefined && location.lng !== undefined) {
    return {
      lat: location.lat,
      lng: location.lng
    };
  }

  return null;
};

const CartMapView: React.FC = () => {
  const iconURL =
    "https://storage.googleapis.com/fooddelivery-7eac6.appspot.com/Icons/cart-icon.png";

  const location = useLocation();

  // Get cart from Redux store FIRST
  const selectedCartFromRedux = useSelector((state: any) => state.cart.selectedCart);

  // Then get from location state (for backward compatibility)
  const locationState = location.state as { carts?: CartList } || {};
  const incomingCarts = locationState.carts;

  // Use Redux cart as primary source, fallback to location state
  // const cartData = selectedCartFromRedux || incomingCarts;
  const { data: cartData, loading: cartDataLoading } = useFirebaseCartById(selectedCartFromRedux?.id || incomingCarts?.id || '');

  const [liveLocation, setLiveLocation] = useState<google.maps.LatLngLiteral | null>(null);
  const [showInfo, setShowInfo] = useState(false);
  // const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (cartData?.liveLocation) {
      const coordinates = extractLatLng(cartData.liveLocation);
      if (coordinates) {
        setLiveLocation(coordinates);
      }
    } else if (cartData?.location) {
      // Fallback to static location if liveLocation is not available
      const coordinates = extractLatLng(cartData.location);
      if (coordinates) {
        setLiveLocation(coordinates);
      }
    }
  }, [cartData]);

  // const handleRefresh = async () => {
  //   setRefreshing(true);

  //   // In a real app, you might want to fetch fresh data here
  //   if (cartData?.liveLocation) {
  //     const coordinates = extractLatLng(cartData.liveLocation);
  //     if (coordinates) {
  //       setLiveLocation(coordinates);
  //     }
  //   }

  //   setTimeout(() => {
  //     setRefreshing(false);
  //   }, 500);
  // };

  // If no cart data, show message
  // if (!cartData) {
  //   return (
  //     <div style={{
  //       width: "100%",
  //       height: "70vh",
  //       display: "flex",
  //       justifyContent: "center",
  //       alignItems: "center",
  //       backgroundColor: "#f8f9fa"
  //     }}>
  //       <div style={{ textAlign: "center" }}>
  //         <h5 className="text-muted">No cart data available</h5>
  //         <p className="text-muted">Please select a cart from the list</p>
  //       </div>
  //     </div>
  //   );
  // }

  if (cartDataLoading) {
    return (
      <div
        style={{
          width: "100%",
          height: "70vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: "42px",
              height: "42px",
              border: "4px solid #e9ecef",
              borderTop: "4px solid #007bff",
              borderRadius: "50%",
              margin: "0 auto 12px",
              animation: "cart-map-spin 0.9s linear infinite",
            }}
          />
          <p style={{ margin: 0, color: "#6c757d", fontWeight: 500 }}>
            Loading cart location...
          </p>
        </div>
        <style>{`@keyframes cart-map-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* <button
        onClick={handleRefresh}
        disabled={refreshing}
        className="refreshButton"
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          padding: '8px 16px',
          backgroundColor: '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: refreshing ? 'not-allowed' : 'pointer',
          opacity: refreshing ? 0.7 : 1
        }}
      >
        {refreshing ? "Refreshing..." : "Refresh Location"}
      </button> */}

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={liveLocation || defaultCenter}
        zoom={15}
        options={{
          disableDefaultUI: false,
          clickableIcons: false,
          styles: [
            {
              featureType: "poi",
              stylers: [{ visibility: "off" }],
            },
          ],
        }}
      >
        {liveLocation && (
          <Marker
            position={liveLocation}
            icon={{
              url: iconURL,
              scaledSize: new window.google.maps.Size(40, 40),
            }}
            onMouseOver={() => setShowInfo(true)}
            onMouseOut={() => setShowInfo(false)}
          />
        )}

        {liveLocation && showInfo && cartData?.displayName && (
          <InfoWindow
            position={liveLocation}
            options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
            onCloseClick={() => setShowInfo(false)}
          >
            <div style={{ padding: "10px", maxWidth: "250px" }}>
              <h5 style={{ margin: "0 0 5px 0" }}>{cartData.displayName}</h5>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                {liveLocation.lat.toFixed(6)}° N, {liveLocation.lng.toFixed(6)}° E
              </p>
              {cartData.cartState && (
                <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
                  Status: <span style={{
                    color: cartData.cartState === 'Online' ? '#28a745' : '#6c757d',
                    fontWeight: 'bold'
                  }}>{cartData.cartState}</span>
                </p>
              )}
            </div>
          </InfoWindow>
        )}
      </GoogleMap>

      {!liveLocation && (
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          <span>📍 Location not available for this cart</span>
        </div>
      )}
    </div>
  );
};

export default CartMapView;